import React, { useContext, useState, useEffect, useRef } from 'react'
import { useFirebase } from 'react-redux-firebase'
import { getFunctions, httpsCallable } from "firebase/functions";

import { AuthContext } from '@/contexts/AuthContext'
import Button from '@/components/Button'
import { asteriskFilter } from '@/utils/asteriskFilter'
import resolveErrors from '@/utils/resolveErrors'
import { toast } from 'react-toastify';
import Toast from '@/components/Toast'
import { CogIcon } from '@heroicons/react/solid';

export default function SettingsGeneral() {

  return (
    <div className="grid gap-6">
      {/* <PhoneForm /> */}
      <AvatarForm />
      <EmailForm />
      <UpdateEmailForm />
    </div>
  )
}

const EmailForm = () => {
  const { auth } = useContext(AuthContext)
  const functions = getFunctions();
  const resendEmailVerificationEmail = httpsCallable(functions, 'resendEmailVerificationEmail');
  const [serverErrors, setServerErrors] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleResend = async () => {
    try {
      setIsSubmitting(true)
      await resendEmailVerificationEmail({ email: auth?.email })
        .then(() => {
          toast(<Toast icon={<CogIcon className="h-6 w-6 text-gray-300" />} text={`A new verification email has been sent.`} />)
        })
    } catch (err) {
      setServerErrors(resolveErrors(err))
    }
    setIsSubmitting(false)
  }
  return (
    <form className="border border-gray-800 rounded">
      <div className="px-4">
        <div className="py-4 border-b border-gray-800">
          <div className="flex items-center space-x-2 mb-2">
            <h2 className="font-medium text-xl">Verify Email</h2>
            {auth.emailVerified && <span className="px-2 py-1 text-xs bg-purple-600 rounded-full leading-none font-medium">Verified</span>}
          </div>
          <p className="text-gray-300">We will never send you any spam.</p>
        </div>
        <div className="py-3">
          <label className="mb-1 block font-medium text-gray-300">Email</label>
          <div className="lg:max-w-[50%] w-full">
            <input value={asteriskFilter(auth.email)} type="text" className="bg-gray-700 border-transparent shadow-md" readOnly />
          </div>
        </div>
      </div>
      <div className="bg-gray-800 bg-opacity-50 border-t border-gray-800 px-4 py-3 flex justify-between">
        <p>{serverErrors}</p>
        <Button disabled={auth.emailVerified} onClick={handleResend} modifier="secondary" rounded="rounded-full" className="py-2.5 px-4">
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              Processing
              <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
          ) : (
            <span>Resend</span>
          )}
        </Button>
      </div>
    </form>
  )
}

const UpdateEmailForm = () => {
  const { auth } = useContext(AuthContext)
  const functions = getFunctions();
  const updateUserEmail = httpsCallable(functions, 'updateUserEmail');
  const [serverErrors, setServerErrors] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const firebase = useFirebase()

  const handleResend = async () => {
    try {
      setIsSubmitting(true)
      await updateUserEmail({ email: newEmail })
        .then(async () => {
          toast(<Toast icon={<CogIcon className="h-6 w-6 text-gray-300" />} text={`Email has been updated. You will now be logged out.`} />)
          await firebase.logout()
        })
    } catch (err) {
      setServerErrors(resolveErrors(err))
    }
    setIsSubmitting(false)
  }
  return (
    <form className="border border-gray-800 rounded">
      <div className="px-4">
        <div className="py-4 border-b border-gray-800">
          <div className="flex items-center space-x-2 mb-2">
            <h2 className="font-medium text-xl">Update Email</h2>
          </div>
          <p className="text-gray-300">Update your login email address.</p>
        </div>
        <div className="py-3">
          <label className="mb-1 block font-medium text-gray-300">Email</label>
          <div className="lg:max-w-[50%] w-full">
            <input value={newEmail} onChange={e => setNewEmail(e.target.value)} type="text" />
          </div>
        </div>
      </div>
      <div className="bg-gray-800 bg-opacity-50 border-t border-gray-800 px-4 py-3 flex justify-between">
        <p>{serverErrors}</p>
        <Button onClick={handleResend} modifier="secondary" rounded="rounded-full" className="py-2.5 px-4">
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              Processing
              <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
          ) : (
            <span>Update</span>
          )}
        </Button>
      </div>
    </form>
  )
}

const PhoneForm = () => {
  return (
    <div className="border border-gray-800 rounded">
      <div className="px-4">
        <div className="py-4 border-b border-gray-800">
          <div className="flex items-center space-x-2 mb-2">
            <h2 className="font-medium text-xl">Phone Number</h2>
            <span className="px-2 py-1 text-xs bg-blue-600 rounded-full leading-none font-medium">Verified</span>
          </div>
          <p className="text-gray-300">We only service areas that are listed in the available country code list.</p>
        </div>
        <div className="py-3 grid gap-4">
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Country Code</label>
            <select>
              <option>+1 Canada</option>
            </select>
          </div>
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Phone Number</label>
            <input type="text" />
          </div>
        </div>
      </div>
      <div className="bg-gray-800 bg-opacity-50 border-t border-gray-800 px-4 py-3 flex justify-end">
        <div className="grid grid-cols-2 gap-2">
          <Button disabled modifier="secondary" className="py-2.5 px-4">Resend</Button>
          <Button disabled className="py-2.5 px-4">Save</Button>
        </div>
      </div>
    </div>
  )
}

const AvatarForm = () => {
  const functions = getFunctions();
  const inputRef = useRef()
  const setProfilePicture = httpsCallable(functions, 'setProfilePicture');
  const { user } = useContext(AuthContext)
  const [avatar, setAvatar] = useState(null)
  const [serverErrors, setServerErrors] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = e => {
    const reader = new FileReader()
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      reader.onloadend = () => {
        setAvatar({
          image: file,
          imageUrl: reader.result
        })
      }
      reader.readAsDataURL(file)
    }
    inputRef.current.focus()
  }
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      await setProfilePicture(avatar.imageUrl)
    } catch (err) {
      setServerErrors(resolveErrors(err))
    }
    setIsSubmitting(false)
  }

  useEffect(() => {
    setAvatar({
      image: null,
      imageUrl: user?.avatarUrl
    })
  }, [user])

  return (
    <div className="border border-gray-800 rounded">
      <div className="px-4">
        <div className="py-4 border-b border-gray-800">
          <h2 className="font-medium text-xl mb-2">Profile Avatar</h2>
          <p className="text-gray-300">Your personal avatar displayed to all users.</p>
        </div>
        <div className="py-3 grid gap-4">
          <div className="flex">
            <div className="relative rounded-full overflow-hidden">
              <img className="relative rounded-full w-40 h-40 object-cover" src={avatar?.imageUrl} alt="" />
              <label
                htmlFor="desktop-user-photo"
                className="absolute inset-0 w-full h-full bg-black bg-opacity-75 flex items-center justify-center text-sm font-medium text-white opacity-0 hover:opacity-100 focus-within:opacity-100"
              >
                <span>Change</span>
                <span className="sr-only"> user photo</span>
                <input
                  ref={inputRef}
                  onChange={handleChange}
                  type="file"
                  id="desktop-user-photo"
                  name="user-photo"
                  accept="image/*"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md"
                />
                <button ref={inputRef}></button>
              </label>
            </div>

          </div>
          {/* <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Country Code</label>
            <select>
              <option>+1 Canada</option>
            </select>
          </div>
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Phone Number</label>
            <input type="text" />
          </div> */}
        </div>
      </div>
      <div className="bg-gray-800 bg-opacity-50 border-t border-gray-800 px-4 py-3 flex justify-between">
        <p>{serverErrors}</p>
        <Button modifier="secondary" rounded="rounded-full" onClick={handleSubmit} className="py-2.5 px-4">
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              Processing
              <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
          ) : (
            <span>Save</span>
          )}
        </Button>
      </div>
    </div>
  )
}